import { createAction, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

/* --------------------------------- Interfaces ------------------------------ */

interface User {
  userId: number;
  name: string;
  email: string;
  role: string;
  roleId: number;
  subRole: string; 
  createdAt: Date;
  updatedAt: Date;
  resourceId: number;
  password: string;
  roleResourceId: number;
}

interface Role {
  roleResourceId: number;
  role: string;
  description: string;
  resourceId: number;
  roleId: number;
  parentId: number;
  createdAt: Date;
  updatedAt: Date;
}

/* Permissions are now returned as an object keyed by resourceId and then by roleResourceId */
interface TransformedPermission {
  read: boolean;
  write: boolean;
}

interface PermissionMap {
  [resourceId: string]: TransformedPermission;
}

interface AllotableResourcePermission {
  resourceId: number;
  resourceName: string;
  actionName: string;
  permissionIds: number[];
}

interface Permission {
  permissionId: number;
  action: string;
  scope: string;
}

interface UserState {
  users: User[];
  roles: Role[];
  allotableResourcePermissions: AllotableResourcePermission[];
  permissions?: PermissionMap; // new field for transformed permissions response
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  users: [],
  roles: [],
  allotableResourcePermissions: [],
  permissions: undefined,
  loading: false,
  error: null,
};

/* --------------------------------- Thunks ------------------------------ */

// Updated fetchUsersAndRoles - now transforms API returned permissions
export const fetchUsersAndRoles = createAsyncThunk(
  "user/fetchUsersAndRoles",
  async (_, { rejectWithValue }) => {
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const token = localStorage.getItem("accessToken");
      console.log(userDetails);
      const response = await axios.get(`${window.env.API_URL}/v1/programs/authorization/users/${userDetails.roleResourceId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // Convert createdAt and updatedAt strings to Date objects for users and roles
      const users = response.data.data.users.map((user: User) => ({
        ...user,
        createdAt: new Date(user.createdAt),
        updatedAt: new Date(user.updatedAt),
      }));
      const roles = response.data.data.roles.map((role: Role) => ({
        ...role,
        createdAt: new Date(role.createdAt),
        updatedAt: new Date(role.updatedAt),
      }));
      
      // // Transforming permissions: API returns an object keyed by resourceId and then roleId
      // const rawPermissions = response.data.data.permissions;
      // const transformedPermissions: PermissionMap = {};
      
      // for (const resourceId in rawPermissions) {
      //   if (Object.prototype.hasOwnProperty.call(rawPermissions, resourceId)) {
      //     transformedPermissions[resourceId] = {};
      //     const rolePermissions = rawPermissions[resourceId];
      //     for (const roleId in rolePermissions) {
      //       if (Object.prototype.hasOwnProperty.call(rolePermissions, roleId)) {
      //         const perm = rolePermissions[roleId];
      //         transformedPermissions[resourceId][roleId] = {
      //           read: perm.read,
      //           write: Boolean(perm.create || perm.update || perm.delete),
      //         };
      //       }
      //     }
      //   }
      // }
      
      return { users, roles };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch users and roles"
      );
    }
  }
);

// Add user
export const addUser = createAsyncThunk("user/addUser", async (user: Omit<User, "userId">, { rejectWithValue }) => {
  try {
    // const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const token = localStorage.getItem("accessToken");
    const response = await axios.post(
      `${window.env.API_URL}/v1/programs/authorization/users/${user.roleId}`,
      {
        name: user.name,
        email: user.email,
        password: user.password,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Failed to add user");
  }
});

// Create principal user
export interface CreatePrincipalUserPayload {
  principalId: number | string;
  name: string;
  email: string;
  password: string;
}

export const createPrincipalUser = createAsyncThunk(
  "user/createPrincipalUser",
  async (payload: CreatePrincipalUserPayload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      const principalId =
        typeof payload.principalId === "string" &&
        payload.principalId.trim() !== "" &&
        !Number.isNaN(Number(payload.principalId))
          ? Number(payload.principalId)
          : payload.principalId;
      const response = await axios.post(
        `${window.env.API_URL}/v1/programs/principals/user`,
        {
          principalId,
          name: payload.name,
          email: payload.email,
          password: payload.password,
          subRole: "user",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || "Failed to create principal user");
    }
  }
);

// Add role
export const addRole = createAsyncThunk("user/addRole", async (role: Omit<Role, "roleId">, { rejectWithValue }) => {
  try {
    const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const token = localStorage.getItem("accessToken");
    const response = await axios.post(
      `${window.env.API_URL}/v1/programs/authorization/role-resources`,
      { role: role.role, description: role.description },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Failed to add role");
  }
});

// Fetch Allotable Resource Permissions
export const fetchAllotableResourcePermissions = createAsyncThunk("user/fetchAllotableResourcePermissions", async (_, { rejectWithValue }) => {
  try {
    const AllotableResourcePermission: AllotableResourcePermission[] = [];
    const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const token = localStorage.getItem("accessToken");
    const response = await axios.get(
      `${window.env.API_URL}/v1/programs/authorization/users/permissions/${userDetails.roleResourceId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    for (const resource in response.data.data) {
      if (Object.prototype.hasOwnProperty.call(response.data.data, resource)) {
     
        for (const action in response.data.data[resource]) {
          const permissionIds = [];
          let resourceId;
          for (const scope in response.data.data[resource][action]) {
            permissionIds.push(response.data.data[resource][action][scope]['permissionId']);
            resourceId = response.data.data[resource][action][scope]['resourceId'];
          }
          AllotableResourcePermission.push({
            resourceId: resourceId,
            resourceName: resource,
            actionName: action,
            permissionIds: permissionIds,
          });
        }
      }
    }
    console.log(AllotableResourcePermission);

    return AllotableResourcePermission;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Failed to fetch allotable resource permissions");
  }
});

// Map roles to user
export const mapRolesToUser = createAsyncThunk("user/mapRolesToUser", async ({ roleId, resourceIds, permissionIds }: { roleId: number; resourceIds: number[]; permissionIds: number[] }, { rejectWithValue }) => {
  try {
    const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const token = localStorage.getItem("accessToken");
    const response = await axios.post(`${window.env.API_URL}/v1/programs/authorization/role-resource-permissions/${roleId}`, { roleId, resourceIds, permissionIds }, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || "Failed to map roles to user");
  }
});

// 1. Add the new thunk to initialize user management combined fetch.
export const initializeUserManagement = createAsyncThunk(
  "user/initializeUserManagement",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Dispatch both thunks concurrently and wait for both responses to complete
      await Promise.all([
        dispatch(fetchUsersAndRoles()).unwrap(),
        dispatch(fetchAllotableResourcePermissions()).unwrap(),
      ]);
      return; // Nothing to return as state updates are handled by individual thunks
    } catch (error: any) {
      return rejectWithValue(error?.message || "Initialization failed");
    }
  }
);

// Add new thunk for adding a role resource permission
export const addRoleResourcePermission = createAsyncThunk(
  "user/addRoleResourcePermission",
  async (
    { roleResourceId, resourceIds, permissionIds }: { roleResourceId: number; resourceIds: number[]; permissionIds: number[] },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.post(
        `${window.env.API_URL}/v1/programs/authorization/role-resource-permissions/${roleResourceId}`,
        { resourceIds, permissionIds },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to add role resource permission"
      );
    }
  }
);

// Add new thunk for fetching permissions by roleResourceId
export const fetchPermissionsByRoleResourceId = createAsyncThunk(
  "user/fetchPermissionsByRoleResourceId",
  async (roleResourceId: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(
        `${window.env.API_URL}/v1/programs/authorization/users/adminportal/permissions/${roleResourceId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Transforming permissions: API returns an object keyed by resourceId
      const rawPermissions = response.data.data;
      const transformedPermissions: PermissionMap = {};
      
      for (const resourceId in rawPermissions) {
        if (Object.prototype.hasOwnProperty.call(rawPermissions, resourceId)) {
          transformedPermissions[resourceId] = {
            read: rawPermissions[resourceId].read,
            write: Boolean(rawPermissions[resourceId].create || rawPermissions[resourceId].update || rawPermissions[resourceId].delete),
          };
        }
      }

      return transformedPermissions; // Return the transformed permissions
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch permissions"
      );
    }
  }
);

// Add new thunk for updating a user
export const updateUser = createAsyncThunk(
  "user/updateUser",
  async (
    { userId, roleResourceId }: { userId: number; roleResourceId: number },
    { rejectWithValue }
  ) => {
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const token = localStorage.getItem("accessToken");
      const response = await axios.put(
        `${window.env.API_URL}/v1/programs/authorization/users/${userDetails.roleResourceId}`,
        { userId, roleResourceId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data; // Return the updated user data
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to update user"
      );
    }
  }
);

/* --------------------------------- Slice ------------------------------ */

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Users and Roles (and Permissions)
      .addCase(fetchUsersAndRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchUsersAndRoles.fulfilled,
        (
          state,
          action: PayloadAction<{
            users: User[];
            roles: Role[];
          }>
        ) => {
          state.users = action.payload.users;
          state.roles = action.payload.roles;
          // Avoid turning off loading here since another thunk might still be pending.
        }
      )
      .addCase(fetchUsersAndRoles.rejected, (state, action) => {
        state.error = action.payload as string;
        // Avoid resetting loading here so that the combined thunk handles it.
      })

      // Add User
      .addCase(addUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.users.push(action.payload);
      })
      .addCase(addUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create Principal User
      .addCase(createPrincipalUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPrincipalUser.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createPrincipalUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Add Role
      .addCase(addRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addRole.fulfilled, (state, action: PayloadAction<Role>) => {
        state.loading = false;
        state.roles.push(action.payload);
      })
      .addCase(addRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Allotable Resource Permissions
      .addCase(fetchAllotableResourcePermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllotableResourcePermissions.fulfilled, (state, action: PayloadAction<AllotableResourcePermission[]>) => {
        state.allotableResourcePermissions = action.payload;
        // As above, keep loading true until the combined thunk completes.
      })
      .addCase(fetchAllotableResourcePermissions.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Map Roles to User
      .addCase(mapRolesToUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(mapRolesToUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        const index = state.users.findIndex((user) => user.userId === action.payload.userId);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(mapRolesToUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // 2. Add extraReducers for the new combined thunk.
      .addCase(initializeUserManagement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeUserManagement.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(initializeUserManagement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Extra reducers for addRoleResourcePermission
      .addCase(addRoleResourcePermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addRoleResourcePermission.fulfilled, (state) => {
        state.loading = false;
        // Optionally, update parts of state if needed.
      })
      .addCase(addRoleResourcePermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Extra reducers for fetchPermissionsByRoleResourceId
      .addCase(fetchPermissionsByRoleResourceId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissionsByRoleResourceId.fulfilled, (state, action: PayloadAction<PermissionMap>) => {
        state.loading = false;
        state.permissions = action.payload;
      })
      .addCase(fetchPermissionsByRoleResourceId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

      // Add new reducer for updating the selected roleId
  },
});

// Export the reducer
export default userSlice.reducer; 